import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import './dayBook.css';
import { getToken } from '../auth';
import {
  FiCalendar, FiRefreshCw, FiArrowDownCircle, FiArrowUpCircle,
  FiRepeat, FiX, FiPlus, FiEdit2, FiTrash2
} from 'react-icons/fi';


const DayBook = () => {
  const today = new Date().toISOString().slice(0, 10);

  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Starting cash modal
  const [showCashModal, setShowCashModal] = useState(false);
  const [cashInput, setCashInput] = useState('');
  const [savingCash, setSavingCash] = useState(false);

  // Transfer modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferAmount, setTransferAmount] = useState('');
  const [transferBankAccountId, setTransferBankAccountId] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [savingTransfer, setSavingTransfer] = useState(false);

  const authConfig = useCallback(() => {
    const token = getToken();
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [dayBookRes, bankAccountsRes] = await Promise.all([
        axios.get(`/day-book?date=${date}`, authConfig()),
        axios.get(`/bank-accounts`, authConfig())
      ]);
      const accounts = Array.isArray(bankAccountsRes.data) ? bankAccountsRes.data : [];
      setData(dayBookRes.data);
      setBankAccounts(accounts);
      setCashInput(String(dayBookRes.data.starting_cash ?? '0'));
    } catch (err) {
      console.error(err);
      setError('Failed to load Day Book data.');
    } finally {
      setLoading(false);
    }
  }, [date, authConfig]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (bankAccounts.length === 0) {
      setTransferBankAccountId('');
      return;
    }

    setTransferBankAccountId(prevAccountId => {
      const stillExists = bankAccounts.some(account => String(account.id) === String(prevAccountId));
      return stillExists ? prevAccountId : String(bankAccounts[0].id);
    });
  }, [bankAccounts]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleSaveCash = async () => {
    try {
      setSavingCash(true);
      await axios.post(
        `/day-book/starting-cash`,
        { entry_date: date, starting_cash: parseFloat(cashInput) || 0 },
        authConfig()
      );
      setShowCashModal(false);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save starting cash');
    } finally {
      setSavingCash(false);
    }
  };

  const handleAddTransfer = async () => {
    if (!transferAmount || parseFloat(transferAmount) <= 0) {
      alert('Enter a valid amount');
      return;
    }
    if (!transferBankAccountId) {
      alert('Select a bank account');
      return;
    }
    try {
      setSavingTransfer(true);
      await axios.post(
        `/day-book/transfer`,
        {
          transfer_date: date,
          amount: parseFloat(transferAmount),
          bank_account_id: parseInt(transferBankAccountId),
          notes: transferNotes
        },
        authConfig()
      );
      setShowTransferModal(false);
      setTransferAmount('');
      setTransferNotes('');
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to record transfer');
    } finally {
      setSavingTransfer(false);
    }
  };

  const handleDeleteTransfer = async (id) => {
    if (!window.confirm('Delete this transfer record?')) return;
    try {
      await axios.delete(`/day-book/transfer/${id}`, authConfig());
      await fetchData();
    } catch (err) {
      alert('Failed to delete transfer');
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const fmt = (n) =>
    'Rs. ' + parseFloat(n || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const typeIcon = (type, direction) => {
    if (type === 'transfer') return <FiRepeat className="tx-icon transfer" />;
    if (direction === 'in') return <FiArrowDownCircle className="tx-icon in" />;
    return <FiArrowUpCircle className="tx-icon out" />;
  };

  const paymentBadge = (payment_type) => {
    if (!payment_type || payment_type === 'cash') return <span className="badge cash">Cash</span>;
    if (payment_type === 'bank') return <span className="badge bank">Bank</span>;
    if (payment_type === 'cheque') return <span className="badge cheque">Cheque</span>;
    if (payment_type === 'transfer') return <span className="badge transfer">Transfer</span>;
    return <span className="badge cash">{payment_type}</span>;
  };

  const txRowClass = (direction) => {
    if (direction === 'in') return 'tx-row in';
    if (direction === 'out') return 'tx-row out';
    return 'tx-row transfer';
  };

  const isToday = date === today;
  const totalBankBalance = data?.summary?.total_bank_balance ??
    bankAccounts.reduce((sum, account) => sum + (parseFloat(account.balance) || 0), 0);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="db-page">
      {/* ── Header ── */}
      <div className="db-header">
        <div className="db-title-block">
          <FiCalendar className="db-title-icon" />
          <div>
            <h1 className="db-title">Day Book</h1>
            <p className="db-subtitle">Daily cash &amp; bank ledger</p>
          </div>
        </div>
        <div className="db-header-actions">
          <input
            type="date"
            className="db-date-input"
            value={date}
            max={today}
            onChange={(e) => setDate(e.target.value)}
          />
          <button className="db-btn icon-btn" onClick={fetchData} title="Refresh">
            <FiRefreshCw />
          </button>
        </div>
      </div>

      {error && <div className="db-error">{error}</div>}

      {loading ? (
        <div className="db-loading">
          <div className="db-spinner" />
          Loading ledger…
        </div>
      ) : data && (
        <>
          {/* ── Summary Cards ── */}
          <div className="db-cards">
            <div className="db-card starting">
              <span className="db-card-label">Starting Cash</span>
              <span className="db-card-value">{fmt(data.starting_cash)}</span>
              {isToday && (
                <button className="db-card-edit" onClick={() => setShowCashModal(true)}>
                  <FiEdit2 size={13} /> Edit
                </button>
              )}
            </div>
            <div className="db-card cash-in">
              <span className="db-card-label">Cash In</span>
              <span className="db-card-value green">+{fmt(data.summary.cash_in)}</span>
            </div>
            <div className="db-card cash-out">
              <span className="db-card-label">Cash Out</span>
              <span className="db-card-value red">−{fmt(data.summary.cash_out)}</span>
            </div>
            <div className="db-card closing-cash">
              <span className="db-card-label">Closing Cash</span>
              <span className={`db-card-value ${data.summary.closing_cash < 0 ? 'red' : 'highlight'}`}>
                {fmt(data.summary.closing_cash)}
              </span>
            </div>
            <div className="db-card bank-in">
              <span className="db-card-label">Bank In</span>
              <span className="db-card-value green">+{fmt(data.summary.bank_in)}</span>
            </div>
            <div className="db-card bank-out">
              <span className="db-card-label">Bank Out</span>
              <span className="db-card-value red">−{fmt(data.summary.bank_out)}</span>
            </div>
            <div className="db-card closing-bank">
              <span className="db-card-label">Bank Net</span>
              <span className={`db-card-value ${data.summary.closing_bank < 0 ? 'red' : 'highlight'}`}>
                {fmt(data.summary.closing_bank)}
              </span>
            </div>
            <div className="db-card total-bank">
              <span className="db-card-label">Bank Balance</span>
              <span className="db-card-value highlight">{fmt(totalBankBalance)}</span>
            </div>
          </div>

          {bankAccounts.length > 0 && (
            <div className="db-bank-balances">
              <div className="db-bank-balances-header">
                <h2 className="db-section-title">Bank Account Balances</h2>
              </div>
              <div className="db-bank-balance-list">
                {bankAccounts.map(account => (
                  <div className="db-bank-balance-item" key={account.id}>
                    <div className="db-bank-identity">
                      <span className="db-bank-name">{account.account_name}</span>
                      <span className="db-bank-number">{account.account_number}</span>
                    </div>
                    <strong>{fmt(account.balance)}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Action Buttons ── */}
          {isToday && (
            <div className="db-actions">
              <button className="db-btn primary" onClick={() => setShowCashModal(true)}>
                Rs. Set Starting Cash
              </button>
              <button className="db-btn secondary" onClick={() => setShowTransferModal(true)}>
                <FiRepeat /> Cash → Bank Transfer
              </button>
            </div>
          )}

          {/* ── Transaction Ledger ── */}
          <div className="db-ledger">
            <div className="db-ledger-header">
              <h2 className="db-section-title">
                Transactions for {new Date(date + 'T00:00:00').toLocaleDateString('en-IN', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                })}
              </h2>
              <span className="db-tx-count">{data.transactions.length} entries</span>
            </div>

            {data.transactions.length === 0 ? (
              <div className="db-empty">
                <FiCalendar size={36} className="db-empty-icon" />
                <p>No transactions recorded for this date.</p>
              </div>
            ) : (
              <div className="db-table-wrap">
                <table className="db-table">
                  <thead>
                    <tr>
                      <th className="col-icon"></th>
                      <th className="col-desc">Description</th>
                      <th className="col-method">Method</th>
                      <th className="col-amount">Amount</th>
                      <th className="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.transactions.map((tx, i) => (
                      <tr key={i} className={txRowClass(tx.direction)}>
                        <td className="col-icon">{typeIcon(tx.type, tx.direction)}</td>
                        <td className="col-desc">
                          <span className="tx-desc">{tx.description}</span>
                          <span className="tx-type-tag">{txTypeLabel(tx.type)}</span>
                        </td>
                        <td className="col-method">{paymentBadge(tx.payment_type)}</td>
                        <td className="col-amount">
                          <span className={`tx-amount ${tx.direction === 'in' ? 'in' : 'out'}`}>
                            {tx.direction === 'in' ? '+' : tx.direction === 'out' ? '−' : '⇄'}
                            {fmt(tx.amount)}
                          </span>
                        </td>
                        <td className="col-actions">
                          {tx.type === 'transfer' && isToday && (
                            <button
                              className="db-del-btn"
                              title="Delete transfer"
                              onClick={() => handleDeleteTransfer(tx.ref_id)}
                            >
                              <FiTrash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Starting Cash Modal ── */}
      {showCashModal && (
        <div className="db-modal-overlay" onClick={() => setShowCashModal(false)}>
          <div className="db-modal" onClick={(e) => e.stopPropagation()}>
            <div className="db-modal-header">
              <h3>Set Starting Cash</h3>
              <button className="db-modal-close" onClick={() => setShowCashModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="db-modal-body">
              <p className="db-modal-desc">
                Enter the cash in hand at the <strong>start of {isToday ? 'today' : date}</strong>.
              </p>
              <label className="db-modal-label">Amount (Rs.)</label>
              <input
                className="db-modal-input"
                type="number"
                min="0"
                step="0.01"
                value={cashInput}
                onChange={(e) => setCashInput(e.target.value)}
                autoFocus
                onWheel={(e) => e.target.blur()}
              />
            </div>
            <div className="db-modal-footer">
              <button className="db-btn ghost" onClick={() => setShowCashModal(false)}>Cancel</button>
              <button className="db-btn primary" onClick={handleSaveCash} disabled={savingCash}>
                {savingCash ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Transfer Modal ── */}
      {showTransferModal && (
        <div className="db-modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="db-modal" onClick={(e) => e.stopPropagation()}>
            <div className="db-modal-header">
              <h3><FiRepeat /> Cash → Bank Transfer</h3>
              <button className="db-modal-close" onClick={() => setShowTransferModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="db-modal-body">
              <p className="db-modal-desc">
                Record cash being moved to a bank account. This reduces cash balance and adds to the selected bank.
              </p>
              <label className="db-modal-label">Bank Account *</label>
              <select
                className="db-modal-input"
                value={transferBankAccountId}
                onChange={(e) => setTransferBankAccountId(e.target.value)}
                disabled={savingTransfer || bankAccounts.length === 0}
              >
                <option value="">Select bank account</option>
                {bankAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.account_name} - {account.account_number}
                  </option>
                ))}
              </select>
              {bankAccounts.length === 0 && (
                <p className="db-modal-hint">Create a bank account before recording a transfer.</p>
              )}
              <label className="db-modal-label">Amount (Rs.) *</label>
              <input
                className="db-modal-input"
                type="number"
                min="0.01"
                step="0.01"
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                placeholder="0.00"
                autoFocus
                onWheel={(e) => e.target.blur()}
              />
              <label className="db-modal-label" style={{ marginTop: '1rem' }}>Notes (optional)</label>
              <input
                className="db-modal-input"
                type="text"
                value={transferNotes}
                onChange={(e) => setTransferNotes(e.target.value)}
                placeholder="e.g. Daily deposit"
              />
            </div>
            <div className="db-modal-footer">
              <button className="db-btn ghost" onClick={() => setShowTransferModal(false)}>Cancel</button>
              <button className="db-btn primary" onClick={handleAddTransfer} disabled={savingTransfer || bankAccounts.length === 0}>
                <FiPlus /> {savingTransfer ? 'Recording…' : 'Record Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function txTypeLabel(type) {
  switch (type) {
    case 'sale': return 'Sale';
    case 'retailer_payment': return 'Payment Received';
    case 'supplier_payment': return 'Paid to Supplier';
    case 'expense': return 'Expense';
    case 'transfer': return 'Cash→Bank';
    case 'bank_deposit': return 'Bank Deposit';
    case 'bank_withdraw': return 'Bank Withdraw';
    case 'cash_refund': return 'Cash Refund';
    case 'credit_settlement_cash': return 'Credit Settled (Cash)';
    default: return type;
  }
}

export default DayBook;
